import {
  AlertCircle,
  Check,
  Clock,
  Loader2,
  Lock,
  Mic,
  PanelRightOpen,
} from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { JudgeBallotProject, JudgingCriterion } from '@/types';
import { CriterionName } from './CriterionName';
import { ProjectLogo } from './ProjectLogo';
import { trackOf } from './scoring';
import { type SaveStatus, useProjectGrades } from './useProjectGrades';

export function SaveIndicator({
  status,
  iconOnly = false,
}: {
  status: SaveStatus | 'invalid';
  // Just the icon, its text as a tooltip (the final check's narrow rows).
  iconOnly?: boolean;
}) {
  const states = {
    idle: null,
    saving: { icon: Loader2, text: 'Guardando…', className: 'text-primary/60', spin: true },
    saved: { icon: Check, text: 'Guardado', className: 'text-primary/60', spin: false },
    error: { icon: AlertCircle, text: 'Error al guardar', className: 'text-destructive', spin: false },
    invalid: { icon: AlertCircle, text: 'Corrige para guardar', className: 'text-destructive', spin: false },
  } as const;
  const state = states[status];
  if (!state) return <span className="h-5" />;

  const Icon = state.icon;
  return (
    <span
      className={cn(
        'flex h-5 items-center gap-1 whitespace-nowrap text-xs',
        state.className,
      )}
      title={iconOnly ? state.text : undefined}
      aria-live="polite"
    >
      <Icon className={cn('h-3 w-3', state.spin && 'animate-spin')} />
      {iconOnly ? <span className="sr-only">{state.text}</span> : state.text}
    </span>
  );
}

function StatusBadge({ project }: { project: JudgeBallotProject }) {
  if (project.status === 'current') {
    return (
      <span className="flex items-center gap-1 bg-primary px-2 py-1 font-bold text-background text-xs uppercase">
        <Mic className="h-3 w-3" />
        En escenario
      </span>
    );
  }
  if (project.status === 'pending') {
    return (
      <span className="flex items-center gap-1 border border-primary/40 px-2 py-1 text-primary/60 text-xs uppercase">
        <Lock className="h-3 w-3" />
        Aún no presenta
      </span>
    );
  }
  // Clicked once, but a project ahead of it in the running order has not presented —
  // it was moved further down and stays locked until its new turn comes.
  if (!project.gradable) {
    return (
      <span className="flex items-center gap-1 border border-primary/40 px-2 py-1 text-primary/60 text-xs uppercase">
        <Clock className="h-3 w-3" />
        Reprogramado
      </span>
    );
  }
  return (
    <span className="border border-primary/60 px-2 py-1 text-primary/80 text-xs uppercase">
      Presentado
    </span>
  );
}

// One project of the stage page: its header (opens the detail panel), the 0–5 grade
// per criterion with the weighted total, and the judge's internal note.
export function ProjectRow({
  stageSlug,
  project,
  criteria,
  readOnly,
  onExpand,
  scrollIntoView = false,
}: {
  stageSlug: string;
  project: JudgeBallotProject;
  criteria: JudgingCriterion[];
  // The judge closed the stage: the grades and the note are shown, never edited.
  readOnly: boolean;
  onExpand: () => void;
  scrollIntoView?: boolean;
}) {
  const grades = useProjectGrades({ stageSlug, project, criteria });
  const { valueOf, errorOf, invalid, errors } = grades;
  const ref = useRef<HTMLDivElement>(null);
  const locked = !project.gradable;
  const current = project.status === 'current';

  // Bring the project on stage into view when it takes the stage.
  useEffect(() => {
    if (scrollIntoView) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [scrollIntoView]);

  return (
    <div
      ref={ref}
      className={cn(
        'flex scroll-mt-8 flex-col gap-4 border-2 p-4 sm:p-5',
        current
          ? 'border-primary bg-primary/10 shadow-[6px_6px_0_0_hsl(var(--primary))]'
          : 'border-primary/40',
        locked && !current && 'opacity-60',
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <button
          type="button"
          onClick={onExpand}
          className="flex min-w-0 items-center gap-3 text-left"
        >
          <span className="w-7 shrink-0 font-bold text-primary/50 text-xl">
            {project.order}
          </span>
          <ProjectLogo project={project} className="h-12 w-12" />
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h2 className="break-words font-bold text-lg text-primary hover:underline">
                {project.name}
              </h2>
              <span className="border border-primary/60 px-1.5 py-0.5 text-primary text-xs">
                {trackOf(project)}
              </span>
              <span className="border border-primary/30 px-1.5 py-0.5 text-primary/70 text-xs">
                {project.teamSlug}
              </span>
            </div>
            {project.oneliner && (
              <p className="line-clamp-2 text-primary/60 text-xs">
                {project.oneliner}
              </p>
            )}
          </div>
        </button>
        <div className="flex shrink-0 items-center justify-between gap-2">
          <StatusBadge project={project} />
          <Button
            size="sm"
            variant="outline"
            onClick={onExpand}
            aria-label={`Ver detalles de ${project.name}`}
          >
            <PanelRightOpen className="mr-1 h-4 w-4" />
            Ver más
          </Button>
        </div>
      </div>

      {!locked && criteria.length > 0 && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-4">
          <div className="grid flex-1 grid-cols-2 gap-x-3 gap-y-1 md:grid-cols-4">
            {criteria.map((criterion) => (
              <label key={criterion.id} className="flex flex-col gap-1">
                <span className="flex min-w-0 items-center gap-1 text-primary/80 text-xs">
                  <CriterionName criterion={criterion} className="min-w-0">
                    <span className="truncate">{criterion.name}</span>
                  </CriterionName>
                  <span className="shrink-0 text-primary/50">
                    ({criterion.weight}%)
                  </span>
                </span>
                <Input
                  inputMode="decimal"
                  placeholder="0 – 5"
                  value={valueOf(criterion.id)}
                  onChange={(e) => grades.setValue(criterion.id, e.target.value)}
                  onBlur={grades.saveNow}
                  aria-invalid={invalid(criterion.id) || undefined}
                  readOnly={readOnly}
                  className={cn(
                    'font-bold text-base placeholder:font-normal placeholder:text-primary/30',
                    readOnly && 'cursor-default border-primary/20 focus-visible:ring-0',
                    // Same border width either way: only the colour changes, so an
                    // error never nudges the field.
                    errorOf(criterion.id) &&
                      'border-destructive text-destructive focus-visible:ring-destructive',
                  )}
                />
                {/* A one-line slot that is always there, so the message appearing or
                    going away does not push the rows below. */}
                {!readOnly && (
                  <span
                    className="h-4 truncate text-destructive text-xs leading-4"
                    title={errorOf(criterion.id) ?? undefined}
                  >
                    {errorOf(criterion.id)}
                  </span>
                )}
              </label>
            ))}
          </div>
          {/* Fixed width, so the save indicator changing its text never resizes the
              grade fields beside it. */}
          <div className="flex items-center justify-between gap-2 sm:w-40 sm:shrink-0 sm:flex-col sm:items-end sm:pt-5">
            <span className="text-primary text-sm">
              Nota{' '}
              <span className="bg-primary px-2 py-0.5 font-bold text-background">
                {grades.weighted === null ? '–' : grades.weighted.toFixed(2)}
              </span>
            </span>
            {!readOnly && <SaveIndicator status={grades.status} />}
          </div>
        </div>
      )}
      {readOnly && project.note && (
        <div className="flex flex-col gap-1">
          <span className="text-primary/80 text-xs">Nota interna</span>
          <p className="whitespace-pre-line border-primary/30 border-l-2 pl-3 text-primary/90 text-sm">
            {project.note}
          </p>
        </div>
      )}
      {!locked && !readOnly && (
        <NoteField grades={grades} />
      )}
      {locked && project.status === 'presented' && (
        <p className="text-primary/60 text-xs">
          Se podrá evaluar cuando pasen los proyectos que ahora van antes que
          él.
        </p>
      )}
      {errors.base && <p className="text-destructive text-sm">{errors.base}</p>}
    </div>
  );
}

// The judge's internal note on a project, saved with the grades.
export function NoteField({
  grades,
  autoFocus = false,
}: {
  grades: ReturnType<typeof useProjectGrades>;
  autoFocus?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-primary/80 text-xs">
        Nota interna{' '}
        <span className="text-primary/50">
          (privada: solo la ven tú y la organización)
        </span>
      </span>
      <Textarea
        rows={2}
        autoFocus={autoFocus}
        placeholder="Comentarios, dudas, cosas a destacar…"
        value={grades.note}
        onChange={(e) => grades.setNote(e.target.value)}
        onBlur={grades.saveNow}
        className={cn(
          'text-sm placeholder:text-primary/30',
          grades.errors.note && 'border-destructive',
        )}
      />
      {grades.errors.note && (
        <span className="text-destructive text-xs">{grades.errors.note}</span>
      )}
    </label>
  );
}
